<script lang="ts" setup>
  import type { CCardItemAlignment, CCardItemData } from '@/components/common/CCardItem/CCardItem.types'
  import type { TypeCardGridComponent } from '@/types/contentful'
  import type { ImgLoading } from '@/types/media'
  import { definedItems } from '@/utils/contentful'

  const props = withDefaults(
    defineProps<{
      data: TypeCardGridComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>
      loading?: ImgLoading
      preload?: boolean
    }>(),
    {
      loading: 'lazy',
      preload: false
    }
  )

  const alignment = computed(() => props.data.fields.alignment.toLowerCase() as CCardItemAlignment)
  const cards = computed(() => transformCardData(props.data))

  /**
   * @description Transform the card data to the format required by the CCardGrid component
   * @param data The card data from CMS
   * @returns The transformed card data
   */
  function transformCardData(data: TypeCardGridComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>): CCardItemData[] {
    const filteredItems = definedItems(data.fields.items) || []

    return filteredItems
      .map((item) => {
        // Filter out items that have no fields (i.e. draft items)
        if (!Object.hasOwn(item, 'fields')) {
          return undefined
        }

        return {
          id: item.sys.id,
          title: item.fields.title || '',
          body: item.fields.body || '',
          button: item.fields.button
            ? {
                title: item.fields.button.fields.title,
                href: item.fields.button.fields.href,
                target: item.fields.button.fields.target
              }
            : undefined,
          secondaryButton: item.fields.secondaryButton
            ? {
                title: item.fields.secondaryButton.fields.title,
                href: item.fields.secondaryButton.fields.href,
                target: item.fields.secondaryButton.fields.target
              }
            : undefined,
          image: item.fields.image
            ? {
                alt: item.fields.image.fields.description,
                url: item.fields.image.fields.file!.url
              }
            : undefined
        }
      })
      .filter((item) => item !== undefined) as CCardItemData[]
  }
</script>

<template>
  <section class="m-card-grid module-paddings-h">
    <CCardGrid
      :id="data.sys.id"
      :alignment
      :background-color="data.fields.base?.fields.backgroundColour"
      :dark-mode="data.fields.base?.fields.theme === 'Dark'"
      :items="cards"
      :loading
      :preload
    />
  </section>
</template>

<style lang="scss" scoped>
  .m-card-grid {
    width: 100%;
    padding: 8px 0;

    background-color: v-bind('data.fields.base?.fields.backgroundColour');

    @include screenMdAndUp {
      padding: 12px 0;
    }

    :deep(.c-card-grid) {
      max-width: 1400px;
      margin: 0 auto;
    }
  }
</style>
